import { useMutation, useQueryClient } from "react-query";

import { deleteTargetMarker } from "api/targets";
import { useGlobalToast } from "components/GlobalToastProvider";
import type { DeleteMarkerScope } from "api/types/markers";
import { invalidateTargetMarkers } from "./invalidateTargetMarkers";

interface DeleteTargetMarkerPayload {
  targetId: number;
  markerId: number;
  /** Omitted: the backend deletes the marker for every target it applies to. */
  scope?: DeleteMarkerScope;
  /** Targets the marker applied to, so their charts are refreshed too. */
  affectedTargetIds?: number[];
}

/**
 * Deletes a chart marker, for every target or only this one (`scope`).
 * Invalidates the markers query of every affected target on success so each
 * overlay drops the marker, or updates its applied targets.
 */
const useDeleteTargetMarker = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: deleteMarkerAction,
    isLoading: isDeletingMarker,
    ...rest
  } = useMutation(
    ({ targetId, markerId, scope }: DeleteTargetMarkerPayload) =>
      deleteTargetMarker(targetId, markerId, scope),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Marker deleted" });
        invalidateTargetMarkers(queryClient, [
          variables.targetId,
          ...(variables.affectedTargetIds ?? []),
        ]);
        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || "Failed to delete marker";
        showToast({ message: errorMessage, errorState: true });
        onError(error);
      },
      ...otherOptions,
    }
  );

  return { deleteMarkerAction, isDeletingMarker, ...rest };
};

export default useDeleteTargetMarker;
